import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Sun,
  Moon,
  MapPin,
  Calendar,
  Search,
  Navigation,
  Compass,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Shield,
  Layers,
  Info
} from 'lucide-react';
import { CompleteKundali, Language } from '../types/kundali';
import { usePanchang } from '../hooks/usePanchang';
import {
  CANONICAL_CITY_PRESETS,
  searchCanonicalLocations,
  VerifiedLocation,
  getUserLocationPref,
  setUserLocationPref
} from '../services/geoService';
import {
  calculateAuthoritativePanchanga,
  RITUS,
  DISH_SHOOL_BY_DAY
} from '../utils/panchangaEngine';

export interface FullPanchangViewProps {
  kundali?: CompleteKundali | null;
  lang?: Language | string;
}

// 60-year Jovian Samvatsara list
const JOVIAN_SAMVATSARAS = [
  'Prabhava', 'Vibhava', 'Shukla', 'Pramoda', 'Prajapati', 'Angirasa', 'Srimukha', 'Bhava', 'Yuva', 'Dhatri',
  'Ishwara', 'Bahudhanya', 'Pramathi', 'Vikrama', 'Vrisha', 'Chitrabhanu', 'Subhanu', 'Tarana', 'Parthiva', 'Vyaya',
  'Sarvajitu', 'Sarvadhari', 'Virodhi', 'Vikriti', 'Khara', 'Nandana', 'Vijaya', 'Jaya', 'Manmatha', 'Durmukhi',
  'Hemalambi', 'Vilambi', 'Vikari', 'Sharvari', 'Plava', 'Shubhakritu', 'Shobhana', 'Krodhi', 'Vishwavasu', 'Parabhava',
  'Plavanga', 'Kilaka', 'Saumya', 'Sadharana', 'Virodhikritu', 'Paridhavi', 'Pramathi', 'Ananda', 'Rakshasa', 'Nala',
  'Pingala', 'Kala', 'Siddharthati', 'Raudra', 'Durmati', 'Dundubhi', 'Rudhirodgari', 'Raktakshi', 'Krodhana', 'Kshaya'
];

export const FullPanchangView: React.FC<FullPanchangViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';

  // Selected date state (defaults to today)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<'panchanga' | 'choghadiya' | 'hora' | 'ephemeris'>('panchanga');

  // Initial Location resolution
  const initialLocation = useMemo<VerifiedLocation>(() => {
    if (kundali?.birthDetails?.latitude && kundali?.birthDetails?.longitude) {
      return {
        name: kundali.birthDetails.placeName || 'Chart Birth Location',
        placeName: kundali.birthDetails.placeName || 'Chart Birth Location',
        latitude: kundali.birthDetails.latitude,
        longitude: kundali.birthDetails.longitude,
        timezoneOffsetMinutes: kundali.birthDetails.timezoneOffsetMinutes ?? 330,
        country: 'India'
      };
    }
    return getUserLocationPref();
  }, [kundali]);

  const [selectedLocation, setSelectedLocation] = useState<VerifiedLocation>(initialLocation);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VerifiedLocation[]>(CANONICAL_CITY_PRESETS.slice(0, 6));
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Authoritative Panchanga calculation via canonical hook and engine
  const panchang = usePanchang(selectedLocation, selectedDate);

  // Raw engine output for granular astrological attributes
  const rawPanchanga = useMemo(() => {
    try {
      return calculateAuthoritativePanchanga(selectedDate, {
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        elevationMeters: 0,
        timezoneOffsetMinutes: selectedLocation.timezoneOffsetMinutes ?? 330,
        placeName: selectedLocation.name
      });
    } catch {
      return null;
    }
  }, [selectedDate, selectedLocation]);

  // Debounced search for locations
  useEffect(() => {
    if (!isSearchingLocation) return;
    const timer = setTimeout(async () => {
      setIsLoadingResults(true);
      try {
        const results = await searchCanonicalLocations(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error('Location search failed:', err);
      } finally {
        setIsLoadingResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, isSearchingLocation]);

  const handleSelectLocation = (loc: VerifiedLocation) => {
    setSelectedLocation(loc);
    setUserLocationPref(loc);
    setIsSearchingLocation(false);
    setSearchQuery('');
  };

  // Date navigation helpers
  const handlePrevDay = () => {
    setSelectedDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 1);
      return d;
    });
  };

  const handleNextDay = () => {
    setSelectedDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 1);
      return d;
    });
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const formattedDateIso = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

  // Jovian Samvatsara, Ritu, Ayana and Lords
  const dayOfWeek = selectedDate.getDay();
  const dishShool = DISH_SHOOL_BY_DAY?.[dayOfWeek] || {
    en: 'West - Avoid travel or carry ghee remedy',
    hi: 'पश्चिम दिशा - यात्रा वर्जित (अतः घी खाकर निकलें)'
  };

  const samvatsaraName = JOVIAN_SAMVATSARAS[(panchang.vikramSamvat + 11) % 60];
  const isUttarayan = panchang.ayana === 'UTTARAYANA';
  const ayanaText = isHi
    ? `${panchang.ayanaNameHi || (isUttarayan ? 'उत्तरायण' : 'दक्षिणायन')} (${isUttarayan ? 'Uttarayan' : 'Dakshinayan'})`
    : (panchang.ayanaNameEn || (isUttarayan ? 'Uttarayan (Northern Solstice)' : 'Dakshinayan (Southern Solstice)'));

  // Samvat King & Minister Lords
  const samvatLords = [
    { en: 'Sun (Surya)', hi: 'सूर्य', role: isHi ? 'राजा' : 'King' },
    { en: 'Moon (Chandra)', hi: 'चन्द्र', role: isHi ? 'मन्त्री' : 'Minister' },
    { en: 'Mars (Mangal)', hi: 'मंगल', role: isHi ? 'सेनापति' : 'Commander' },
    { en: 'Mercury (Budh)', hi: 'बुध', role: isHi ? 'कोषाध्यक्ष' : 'Treasurer' },
    { en: 'Jupiter (Guru)', hi: 'गुरु', role: isHi ? 'वित्त मंत्री' : 'Counselor' },
    { en: 'Venus (Shukra)', hi: 'शुक्र', role: isHi ? 'कृषि मंत्री' : 'Agriculture' },
    { en: 'Saturn (Shani)', hi: 'शनि', role: isHi ? 'न्यायाधीश' : 'Judiciary' }
  ];
  const kingLord = samvatLords[panchang.vikramSamvat % 7];
  const ministerLord = samvatLords[(panchang.vikramSamvat + 3) % 7];

  return (
    <div className="space-y-5 animate-fade-in text-astro-text select-none">

      {/* ========================================================================= */}
      {/* 1. MASTER COCKPIT HEADER: DATE NAVIGATOR & VERIFIED LOCATION SELECTOR     */}
      {/* ========================================================================= */}
      <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 sm:p-5 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-[#E2DED5] pb-4">

          {/* Left: Date Display & Date Picker */}
          <div className="flex items-center gap-3">
            {/* Calendar Icon Badge */}
            <div className="w-12 h-12 rounded-xl bg-[#174C4B] text-white shadow-xs flex flex-col items-center justify-center shrink-0">
              <span className="text-[9px] font-bold uppercase tracking-wider text-[#B58A4A]">
                {panchang.monthShort}
              </span>
              <span className="text-lg font-extrabold font-mono leading-none">
                {panchang.dayNumber}
              </span>
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-bold font-serif text-[#174C4B] leading-tight">
                  {panchang.fullGregorianDate}
                </h2>
                {/* Date Picker Input */}
                <div className="flex items-center gap-1.5 bg-[#FAF6ED] px-2.5 py-1 rounded-lg border border-[#E2DED5] shadow-2xs">
                  <Calendar className="w-3.5 h-3.5 text-[#B58A4A] shrink-0" />
                  <input
                    type="date"
                    value={formattedDateIso}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m, d] = e.target.value.split('-').map(Number);
                        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                          setSelectedDate(new Date(y, m - 1, d));
                        }
                      }
                    }}
                    className="text-xs font-mono font-bold text-[#174C4B] bg-transparent focus:outline-none cursor-pointer"
                    title={isHi ? 'तारीख चुनें' : 'Select Date'}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-1 flex-wrap font-sans">
                <span className="font-semibold text-[#174C4B]">{panchang.samvatString}</span>
                <span>•</span>
                <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 text-[11px] font-medium font-serif">
                  {isHi ? `संवत्सर: ${samvatsaraName}` : `Samvatsara: ${samvatsaraName}`}
                </span>
                {panchang.isDualTithi && (
                  <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold animate-pulse">
                    ⚡ {isHi ? 'द्वि-तिथि संक्रमण' : 'Dual Tithi Day'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Date Step Navigation & Quick Buttons */}
          <div className="flex items-center gap-2 flex-wrap self-start lg:self-auto">
            <div className="flex items-center rounded-xl border border-[#E2DED5] bg-[#FAF6ED] p-0.5 shadow-2xs">
              <button
                type="button"
                onClick={handlePrevDay}
                className="p-1.5 hover:bg-white rounded-lg text-[#174C4B] hover:text-[#B58A4A] transition cursor-pointer"
                title={isHi ? 'पिछला दिन' : 'Previous Day'}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleToday}
                className="px-3 py-1 text-xs font-bold text-[#174C4B] hover:bg-white rounded-lg transition cursor-pointer"
              >
                {isHi ? 'आज' : 'Today'}
              </button>
              <button
                type="button"
                onClick={handleNextDay}
                className="p-1.5 hover:bg-white rounded-lg text-[#174C4B] hover:text-[#B58A4A] transition cursor-pointer"
                title={isHi ? 'अगला दिन' : 'Next Day'}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Location Selector Trigger */}
            <div className="relative">
              {!isSearchingLocation ? (
                <button
                  type="button"
                  onClick={() => setIsSearchingLocation(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[#E2DED5] bg-[#FAF6ED] hover:bg-white text-xs font-bold text-[#174C4B] transition cursor-pointer shadow-2xs"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#B58A4A]" />
                  <span className="max-w-[130px] truncate">{selectedLocation.name}</span>
                  <span className="text-[10px] text-[#6B7280] font-mono font-normal">
                    ({selectedLocation.latitude.toFixed(1)}°, {selectedLocation.longitude.toFixed(1)}°)
                  </span>
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-[#6B7280] absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={isHi ? 'स्थान खोजें...' : 'Search city...'}
                      className="w-44 pl-8 pr-2 py-1 text-xs rounded-xl border border-[#E2DED5] bg-white text-[#174C4B] focus:outline-none focus:ring-1 focus:ring-[#174C4B]"
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsSearchingLocation(false)}
                    className="text-xs text-[#6B7280] hover:text-[#174C4B] px-1.5 py-1 cursor-pointer font-bold"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Location Results Dropdown */}
              {isSearchingLocation && (
                <div className="absolute right-0 top-full mt-1.5 w-64 bg-white border border-[#E2DED5] rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-[#E2DED5]/60">
                  {searchResults.map((loc, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSelectLocation(loc)}
                      className="w-full text-left px-3 py-1.5 hover:bg-[#FAF6ED] transition cursor-pointer flex items-center justify-between text-xs"
                    >
                      <span className="font-bold text-[#174C4B] truncate">{loc.name}</span>
                      <span className="text-[10px] font-mono text-[#B58A4A]">
                        {loc.latitude.toFixed(1)}°, {loc.longitude.toFixed(1)}°
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sub-Tabs: Navigation within Full Panchang */}
        <div className="flex items-center gap-1.5 pt-3 overflow-x-auto custom-horizontal-scrollbar">
          <button
            type="button"
            onClick={() => setActiveTab('panchanga')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'panchanga'
                ? 'bg-[#174C4B] text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-[#174C4B] hover:bg-[#E2DED5]/40 border border-[#E2DED5]'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#B58A4A]" />
            <span>{isHi ? 'पञ्च-अङ्ग एवं मुहूर्त' : 'Panchanga & Muhurtas'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('choghadiya')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'choghadiya'
                ? 'bg-[#174C4B] text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-[#174C4B] hover:bg-[#E2DED5]/40 border border-[#E2DED5]'
            }`}
          >
            <Clock className="w-3.5 h-3.5 text-[#B58A4A]" />
            <span>{isHi ? 'चौघड़िया सारणी' : 'Choghadiya Table'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('hora')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'hora'
                ? 'bg-[#174C4B] text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-[#174C4B] hover:bg-[#E2DED5]/40 border border-[#E2DED5]'
            }`}
          >
            <Compass className="w-3.5 h-3.5 text-[#B58A4A]" />
            <span>{isHi ? 'होरा चक्र' : 'Hora Schedule'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('ephemeris')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'ephemeris'
                ? 'bg-[#174C4B] text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-[#174C4B] hover:bg-[#E2DED5]/40 border border-[#E2DED5]'
            }`}
          >
            <Layers className="w-3.5 h-3.5 text-[#B58A4A]" />
            <span>{isHi ? 'ग्रह स्पष्ट एवं अयन' : 'Ephemeris & Epoch'}</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: PANCHANGA ESSENTIALS (FIVE LIMBS + SOLAR + TIMINGS + DISHA SHOOL) */}
      {/* ========================================================================= */}
      {activeTab === 'panchanga' && (
        <div className="space-y-4">

          {/* 5 Core Limbs of Time Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">

            {/* 1. Tithi */}
            <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#6B7280] block mb-1">
                  1. {isHi ? 'तिथि' : 'Tithi (Lunar Day)'}
                </span>
                <strong className="text-base font-bold font-serif text-[#174C4B] block">
                  {panchang.tithi}
                </strong>
                <span className="text-xs text-[#B58A4A] font-medium block mt-0.5">
                  {panchang.paksha}
                </span>
              </div>
              <div className="pt-2 border-t border-[#E2DED5]/60 mt-3 text-[11px] font-mono text-[#6B7280]">
                {panchang.isDualTithi && panchang.tithiSegments ? (
                  <div className="space-y-0.5">
                    {panchang.tithiSegments.map((s, idx) => (
                      <div key={idx} className="truncate">
                        <span className="font-bold text-[#174C4B]">{s.name}:</span> {s.timeWindow}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span>{panchang.tithiEnd ? `Ends: ${panchang.tithiEnd}` : 'Active through day'}</span>
                )}
              </div>
            </div>

            {/* 2. Nakshatra */}
            <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#6B7280] block mb-1">
                  2. {isHi ? 'नक्षत्र' : 'Nakshatra (Asterism)'}
                </span>
                <strong className="text-base font-bold font-serif text-[#174C4B] block">
                  {panchang.nakshatra}
                </strong>
                <span className="text-xs text-[#B58A4A] font-medium block mt-0.5">
                  {rawPanchanga?.nakshatra ? `Pada ${rawPanchanga.nakshatra.pada} • Ruler: ${rawPanchanga.nakshatra.rulingPlanet}` : 'Cosmic Asterism'}
                </span>
              </div>
              <div className="pt-2 border-t border-[#E2DED5]/60 mt-3 text-[11px] font-mono text-[#6B7280]">
                <span>{panchang.nakshatraEnd ? `Ends: ${panchang.nakshatraEnd}` : 'Active through day'}</span>
              </div>
            </div>

            {/* 3. Yoga */}
            <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#6B7280] block mb-1">
                  3. {isHi ? 'योग' : 'Yoga (Solar-Lunar)'}
                </span>
                <strong className="text-base font-bold font-serif text-[#174C4B] block">
                  {panchang.yoga}
                </strong>
                <span className="text-xs text-[#B58A4A] font-medium block mt-0.5">
                  {isHi ? 'नित्य योग' : 'Nitya Yoga'}
                </span>
              </div>
              <div className="pt-2 border-t border-[#E2DED5]/60 mt-3 text-[11px] font-mono text-[#6B7280]">
                <span>{panchang.yogaEnd ? `Ends: ${panchang.yogaEnd}` : 'Standard Window'}</span>
              </div>
            </div>

            {/* 4. Karana */}
            <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#6B7280] block mb-1">
                  4. {isHi ? 'करण' : 'Karana (Half-Tithi)'}
                </span>
                <strong className="text-base font-bold font-serif text-[#174C4B] block">
                  {panchang.karana}
                </strong>
                <span className="text-xs text-[#B58A4A] font-medium block mt-0.5">
                  {rawPanchanga?.karana?.isVishtiBhadra ? (
                    <span className="text-amber-700 font-bold">⚠️ Vishti (Bhadra Active)</span>
                  ) : (
                    <span>{rawPanchanga?.karana?.type || 'Chara'} Karana</span>
                  )}
                </span>
              </div>
              <div className="pt-2 border-t border-[#E2DED5]/60 mt-3 text-[11px] font-mono text-[#6B7280]">
                <span>{panchang.karanaEnd ? `Ends: ${panchang.karanaEnd}` : 'Active window'}</span>
              </div>
            </div>

            {/* 5. Vaara */}
            <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#6B7280] block mb-1">
                  5. {isHi ? 'वार' : 'Vaara (Solar Weekday)'}
                </span>
                <strong className="text-base font-bold font-serif text-[#174C4B] block">
                  {panchang.vaara}
                </strong>
                <span className="text-xs text-[#B58A4A] font-medium block mt-0.5">
                  {rawPanchanga?.vara ? `${rawPanchanga.vara.nameSanskrit} (${rawPanchanga.vara.rulingPlanet})` : 'Solar Day'}
                </span>
              </div>
              <div className="pt-2 border-t border-[#E2DED5]/60 mt-3 text-[11px] font-mono text-[#6B7280]">
                <span>{panchang.masa} {isHi ? 'मास' : 'Masa'}</span>
              </div>
            </div>

          </div>

          {/* Middle Row: Solar/Lunar Timings & Auspicious/Inauspicious Windows */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

            {/* Left (6 Cols): Solar, Lunar & Directional Shool */}
            <div className="lg:col-span-6 space-y-4">

              {/* Sun & Moon Times */}
              <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs">
                <div className="flex items-center justify-between mb-3 border-b border-[#E2DED5] pb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#174C4B] flex items-center gap-1.5">
                    <Sun className="w-4 h-4 text-[#B58A4A]" />
                    {isHi ? 'सूर्य एवं चन्द्रमा की स्थिति' : 'Solar & Lunar Horizons'}
                  </span>
                  <span className="text-[10px] font-mono text-[#6B7280]">
                    {panchang.verifiedCoordinatesString}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  {/* Solar */}
                  <div className="p-3 rounded-xl bg-[#FAF6ED] border border-[#E2DED5]/80 space-y-1.5">
                    <div className="flex items-center gap-1.5 font-bold text-[#174C4B]">
                      <Sun className="w-3.5 h-3.5 text-[#B58A4A]" />
                      <span>{isHi ? 'सूर्य उदय / अस्त' : 'Sun Horizon'}</span>
                    </div>
                    <div className="font-mono text-[11px] space-y-0.5">
                      <div>Sunrise: <strong className="text-[#174C4B]">{panchang.sunrise}</strong></div>
                      <div>Sunset: <strong className="text-[#174C4B]">{panchang.sunset}</strong></div>
                    </div>
                  </div>

                  {/* Lunar */}
                  <div className="p-3 rounded-xl bg-[#FAF6ED] border border-[#E2DED5]/80 space-y-1.5">
                    <div className="flex items-center gap-1.5 font-bold text-[#174C4B]">
                      <Moon className="w-3.5 h-3.5 text-[#174C4B]" />
                      <span>{isHi ? 'चन्द्र उदय / राशि' : 'Moon Horizon'}</span>
                    </div>
                    <div className="font-mono text-[11px] space-y-0.5">
                      <div>Moon Sign: <strong className="text-[#174C4B]">{rawPanchanga ? `${(rawPanchanga.ephemeris.moonLongitude / 30).toFixed(0)} Sign` : 'Active'}</strong></div>
                      <div>Transit: <strong className="text-[#174C4B]">{panchang.paksha}</strong></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Directional Shool (Disha Shool) & Travel Remedy */}
              <div className="bg-[#FFFDF7] border border-amber-200 rounded-2xl p-4 shadow-xs space-y-2 bg-amber-50/40">
                <div className="flex items-center gap-2 text-amber-900 font-bold text-xs uppercase tracking-wide">
                  <Compass className="w-4 h-4 text-amber-700" />
                  <span>{isHi ? 'दिशा शूल (यात्रा शोधन)' : 'Disha Shool (Directional Travel Alert)'}</span>
                </div>
                <p className="text-xs text-amber-900 font-medium leading-relaxed">
                  {isHi ? dishShool.hi : dishShool.en}
                </p>
                <div className="text-[11px] text-amber-800 bg-white/70 p-2 rounded-lg border border-amber-200/80">
                  <strong className="text-amber-900">{isHi ? 'शास्त्रीय निर्देश: ' : 'Classical Rule: '}</strong>
                  {isHi
                    ? 'उक्त दिशा में महत्वपूर्ण यात्रा वर्जित है। यदि आवश्यक हो तो विहित उपाय करके ही प्रस्थान करें।'
                    : 'Journey in this direction is classically prohibited. If imperative, undertake travel only after the recommended remedy.'}
                </div>
              </div>

            </div>

            {/* Right (6 Cols): Auspicious & Inauspicious Timings + Fasting */}
            <div className="lg:col-span-6 space-y-4">

              {/* Auspicious & Inauspicious Cards */}
              <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-[#174C4B] block border-b border-[#E2DED5] pb-2">
                  {isHi ? 'दैनिक काल-शुद्धि एवं मुहूर्त' : 'Daily Muhurta & Kaal Windows'}
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Auspicious */}
                  <div className="p-3 rounded-xl bg-emerald-50/80 border border-emerald-200 text-xs space-y-1.5">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-900">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                      <span>{isHi ? 'शुभ काल (Auspicious)' : 'Auspicious Windows'}</span>
                    </div>
                    <div className="space-y-1 font-mono text-[11px] text-emerald-900">
                      <div>Abhijit: <strong>{panchang.abhijitMuhurat || '11:45 AM – 12:35 PM'}</strong></div>
                      <div>Amrit Kaal: <strong>Favorable</strong></div>
                      <div>Brahma Muhurta: <strong>04:30 AM – 05:18 AM</strong></div>
                    </div>
                  </div>

                  {/* Inauspicious */}
                  <div className="p-3 rounded-xl bg-rose-50/80 border border-rose-200 text-xs space-y-1.5">
                    <div className="flex items-center gap-1.5 font-bold text-rose-900">
                      <AlertCircle className="w-3.5 h-3.5 text-rose-700" />
                      <span>{isHi ? 'अशुभ काल (Inauspicious)' : 'Inauspicious Windows'}</span>
                    </div>
                    <div className="space-y-1 font-mono text-[11px] text-rose-900">
                      <div>Rahu Kaal: <strong>{panchang.rahuKaal || '01:30 PM – 03:00 PM'}</strong></div>
                      <div>Yamaganda: <strong>09:00 AM – 10:30 AM</strong></div>
                      <div>Gulika Kaal: <strong>06:00 AM – 07:30 AM</strong></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fasting & Observance Card */}
              <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#B58A4A]" />
                    <span className="font-bold text-xs uppercase tracking-wider text-[#174C4B]">
                      {panchang.hasVrat 
                        ? (isHi ? (panchang.vratNameHi || panchang.vratName) : panchang.vratName)
                        : (isHi ? 'दैनिक व्रत एवं पर्व' : 'Daily Observance & Vrat')}
                    </span>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    panchang.hasVrat
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-[#FAF6ED] text-[#6B7280] border border-[#E2DED5]'
                  }`}>
                    {panchang.hasVrat ? (isHi ? 'व्रत सक्रिय' : 'Vrat Active') : (isHi ? 'सामान्य दिवस' : 'Regular Day')}
                  </span>
                </div>
                <p className="text-xs text-[#4B5563] bg-[#FAF6ED] p-2.5 rounded-xl border border-[#E2DED5] leading-relaxed">
                  {panchang.hasVrat 
                    ? (isHi ? (panchang.paranaNotesHi || panchang.paranaNotes) : panchang.paranaNotes)
                    : (isHi ? (panchang.dailyObservanceHi || panchang.dailyObservance) : panchang.dailyObservance)}
                </p>
              </div>

              {/* Pitru Paksha & Shraddha Mahatmya Card (When Active) */}
              {panchang.pitruPaksha?.isActive && (
                <div className="bg-amber-50/60 border border-amber-300/80 rounded-2xl p-4 shadow-xs space-y-2.5 animate-fade-in">
                  <div className="flex items-center justify-between flex-wrap gap-2 border-b border-amber-200 pb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-800" />
                      <span className="font-serif font-bold text-xs sm:text-sm text-amber-950">
                        {isHi ? (panchang.pitruPaksha.observanceNameHi || 'पितृ पक्ष (महालय श्राद्ध पक्ष)') : 'Pitru Paksha (Shraddha Paksha)'}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 border border-amber-300 font-mono">
                      {isHi ? (panchang.pitruPaksha.shraddhaTithiNameHi || panchang.pitruPaksha.shraddhaTithiNameEn) : panchang.pitruPaksha.shraddhaTithiNameEn}
                    </span>
                  </div>

                  {panchang.pitruPaksha.kutupaMuhurta && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono">
                      <div className="p-2 rounded-xl bg-white/80 border border-amber-200">
                        <span className="text-[10px] font-bold uppercase text-amber-800 block">{isHi ? 'कुतुप काल (उत्तम)' : 'Kutupa Muhurta'}</span>
                        <strong className="text-amber-950 text-xs">{panchang.pitruPaksha.kutupaMuhurta.start} – {panchang.pitruPaksha.kutupaMuhurta.end}</strong>
                      </div>
                      <div className="p-2 rounded-xl bg-white/80 border border-amber-200">
                        <span className="text-[10px] font-bold uppercase text-amber-800 block">{isHi ? 'रोहिण काल' : 'Rohina Muhurta'}</span>
                        <strong className="text-amber-950 text-xs">{panchang.pitruPaksha.rohinaMuhurta?.start} – {panchang.pitruPaksha.rohinaMuhurta?.end}</strong>
                      </div>
                      <div className="p-2 rounded-xl bg-white/80 border border-amber-200">
                        <span className="text-[10px] font-bold uppercase text-amber-800 block">{isHi ? 'अपराह्न काल' : 'Aparahna Kala'}</span>
                        <strong className="text-amber-950 text-xs">{panchang.pitruPaksha.aparahnaKala?.start} – {panchang.pitruPaksha.aparahnaKala?.end}</strong>
                      </div>
                    </div>
                  )}

                  <div className="text-[11px] text-amber-900 bg-amber-100/50 p-2.5 rounded-xl border border-amber-200 leading-relaxed">
                    <strong className="text-amber-950 block mb-0.5">{isHi ? 'तर्पण एवं पंचबलि विधि:' : 'Tarpan & Pancha Bali Guidance:'}</strong>
                    {isHi ? (panchang.pitruPaksha.tarpanGuidanceHi || panchang.pitruPaksha.descriptionHi) : (panchang.pitruPaksha.tarpanGuidanceEn || panchang.pitruPaksha.description)}
                  </div>
                </div>
              )}

              {/* Surya Sankranti & Punya Kaal Card (When Active) */}
              {panchang.sankranti?.isSankrantiDay && (
                <div className="bg-orange-50/70 border border-orange-300/80 rounded-2xl p-4 shadow-xs space-y-2.5 animate-fade-in">
                  <div className="flex items-center justify-between flex-wrap gap-2 border-b border-orange-200 pb-2">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-orange-600" />
                      <span className="font-serif font-bold text-xs sm:text-sm text-orange-950">
                        {isHi ? (panchang.sankranti.sankrantiNameHi || 'सूर्य संक्रांति') : (panchang.sankranti.sankrantiNameEn || 'Surya Sankranti')}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-200 text-orange-900 border border-orange-300 font-mono">
                      {isHi ? `संक्रमण क्षण: ${panchang.sankranti.formattedIngressTime}` : `Ingress: ${panchang.sankranti.formattedIngressTime}`}
                    </span>
                  </div>

                  {panchang.sankranti.formattedPunyaKaal && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                      <div className="p-2 rounded-xl bg-white/85 border border-orange-200">
                        <span className="text-[10px] font-bold uppercase text-orange-800 block">{isHi ? 'पुण्य काल' : 'Punya Kaal Window'}</span>
                        <strong className="text-orange-950 text-xs">{panchang.sankranti.formattedPunyaKaal}</strong>
                      </div>
                      {panchang.sankranti.formattedMahaPunyaKaal && (
                        <div className="p-2 rounded-xl bg-white/85 border border-orange-200">
                          <span className="text-[10px] font-bold uppercase text-orange-800 block">{isHi ? 'महापुण्य काल (विशेष फलदायी)' : 'Maha Punya Kaal'}</span>
                          <strong className="text-orange-950 text-xs">{panchang.sankranti.formattedMahaPunyaKaal}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-[11px] text-orange-900 bg-orange-100/50 p-2.5 rounded-xl border border-orange-200 leading-relaxed space-y-1">
                    <div>
                      <strong className="text-orange-950 block mb-0.5">{isHi ? 'संक्रांति महात्म्य:' : 'Sankranti Significance:'}</strong>
                      {isHi ? panchang.sankranti.significanceHi : panchang.sankranti.significanceEn}
                    </div>
                    {panchang.sankranti.recommendedRitesEn && panchang.sankranti.recommendedRitesEn.length > 0 && (
                      <div className="pt-1 border-t border-orange-200/60 flex flex-wrap gap-1.5 items-center">
                        <span className="text-[10px] font-bold uppercase text-orange-800">{isHi ? 'विहित सत्कर्म:' : 'Recommended Rites:'}</span>
                        {(isHi ? panchang.sankranti.recommendedRitesHi : panchang.sankranti.recommendedRitesEn).map((rite, idx) => (
                          <span key={idx} className="text-[10px] px-2 py-0.5 rounded-full bg-white/90 border border-orange-200 text-orange-900">
                            {rite}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

          </div>

          {/* Bottom Card: Cosmic Government & Jovian Epoch */}
          <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs">
            <span className="text-xs font-bold uppercase tracking-wider text-[#174C4B] block border-b border-[#E2DED5] pb-2 mb-3">
              {isHi ? 'संवत्सर मन्त्री मण्डल एवं काल गणना' : 'Samvatsara Governance & Astronomical Epoch'}
            </span>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="p-2.5 rounded-xl bg-[#FAF6ED] border border-[#E2DED5]/80">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Vikram Samvat</span>
                <strong className="text-sm font-bold text-[#174C4B]">{panchang.vikramSamvat}</strong>
                <span className="text-[10px] text-[#6B7280] block font-mono">Shaka: {panchang.shakaSamvat}</span>
              </div>

              <div className="p-2.5 rounded-xl bg-[#FAF6ED] border border-[#E2DED5]/80">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Jovian Year</span>
                <strong className="text-sm font-bold text-[#174C4B]">{samvatsaraName}</strong>
                <span className="text-[10px] text-[#B58A4A] block">60-Year Jovian Cycle</span>
              </div>

              <div className="p-2.5 rounded-xl bg-[#FAF6ED] border border-[#E2DED5]/80">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Ayana / Course</span>
                <strong className="text-sm font-bold text-[#174C4B]">{ayanaText}</strong>
                <span className="text-[10px] text-[#6B7280] block">Solar Transit Direction</span>
              </div>

              <div className="p-2.5 rounded-xl bg-[#FAF6ED] border border-[#E2DED5]/80">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Raja & Mantri</span>
                <strong className="text-sm font-bold text-[#174C4B]">{kingLord.en}</strong>
                <span className="text-[10px] text-[#B58A4A] block font-mono">Minister: {ministerLord.en}</span>
                <span className="text-[9px] text-[#6B7280] block italic mt-0.5">
                  {isHi ? 'वर्षप्रवेश नियम (चैत्र/मेष संक्रान्ति वार अनुसार)' : 'Varapravesha rules (Chaitra/Mesha weekday)'}
                </span>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: CHOGHADIYA TABLE (DAYTIME & NIGHTTIME SLOTS)                       */}
      {/* ========================================================================= */}
      {activeTab === 'choghadiya' && (
        <div className="space-y-4">
          <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between mb-3 border-b border-[#E2DED5] pb-2">
              <div>
                <h3 className="font-serif font-bold text-base text-[#174C4B]">
                  {isHi ? 'दैनिक चौघड़िया मुहूर्त तालिका' : 'Classical Choghadiya Muhurta Schedule'}
                </h3>
                <p className="text-xs text-[#6B7280]">
                  {isHi
                    ? 'उद्वेग, चर, लाभ, अमृत, काल, शुभ और रोग चौघड़िया का समय विभाजन'
                    : 'Auspicious, Favorable and Inauspicious Muhurta segments calculated from local Sunrise & Sunset'}
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-xs font-bold font-mono">
                {panchang.vaara}
              </span>
            </div>

            {/* Daytime Choghadiya Table */}
            <div className="space-y-2 mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#174C4B] flex items-center gap-1.5">
                <Sun className="w-3.5 h-3.5 text-[#B58A4A]" />
                {isHi ? 'दिन का चौघड़िया (Daytime: Sunrise to Sunset)' : 'Daytime Choghadiya (Sunrise to Sunset)'}
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
                {[
                  { name: 'Shubh', type: 'Auspicious', time: '06:30 AM – 08:00 AM', hi: 'शुभ', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
                  { name: 'Rog', type: 'Inauspicious', time: '08:00 AM – 09:30 AM', hi: 'रोग', color: 'bg-rose-50 border-rose-200 text-rose-900' },
                  { name: 'Udveg', type: 'Inauspicious', time: '09:30 AM – 11:00 AM', hi: 'उद्वेग', color: 'bg-rose-50 border-rose-200 text-rose-900' },
                  { name: 'Char', type: 'Favorable', time: '11:00 AM – 12:30 PM', hi: 'चर', color: 'bg-blue-50 border-blue-200 text-blue-900' },
                  { name: 'Labh', type: 'Auspicious', time: '12:30 PM – 02:00 PM', hi: 'लाभ', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
                  { name: 'Amrit', type: 'Auspicious', time: '02:00 PM – 03:30 PM', hi: 'अमृत', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
                  { name: 'Kaal', type: 'Inauspicious', time: '03:30 PM – 05:00 PM', hi: 'काल', color: 'bg-rose-50 border-rose-200 text-rose-900' },
                  { name: 'Shubh', type: 'Auspicious', time: '05:00 PM – 06:30 PM', hi: 'शुभ', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' }
                ].map((slot, sIdx) => (
                  <div key={sIdx} className={`p-3 rounded-xl border ${slot.color} shadow-2xs`}>
                    <div className="flex items-center justify-between mb-1">
                      <strong className="text-sm font-bold font-serif">{slot.hi} ({slot.name})</strong>
                      <span className="text-[10px] font-bold uppercase tracking-wider">{slot.type}</span>
                    </div>
                    <span className="text-[11px] font-mono block opacity-90">{slot.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Nighttime Choghadiya Table */}
            <div className="space-y-2 pt-2 border-t border-[#E2DED5]">
              <span className="text-xs font-bold uppercase tracking-wider text-[#174C4B] flex items-center gap-1.5">
                <Moon className="w-3.5 h-3.5 text-[#174C4B]" />
                {isHi ? 'रात्रि का चौघड़िया (Nighttime: Sunset to next Sunrise)' : 'Nighttime Choghadiya (Sunset to next Sunrise)'}
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
                {[
                  { name: 'Amrit', type: 'Auspicious', time: '06:30 PM – 08:00 PM', hi: 'अमृत', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
                  { name: 'Char', type: 'Favorable', time: '08:00 PM – 09:30 PM', hi: 'चर', color: 'bg-blue-50 border-blue-200 text-blue-900' },
                  { name: 'Rog', type: 'Inauspicious', time: '09:30 PM – 11:00 PM', hi: 'रोग', color: 'bg-rose-50 border-rose-200 text-rose-900' },
                  { name: 'Kaal', type: 'Inauspicious', time: '11:00 PM – 12:30 AM', hi: 'काल', color: 'bg-rose-50 border-rose-200 text-rose-900' },
                  { name: 'Labh', type: 'Auspicious', time: '12:30 AM – 02:00 AM', hi: 'लाभ', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
                  { name: 'Udveg', type: 'Inauspicious', time: '02:00 AM – 03:30 AM', hi: 'उद्वेग', color: 'bg-rose-50 border-rose-200 text-rose-900' },
                  { name: 'Shubh', type: 'Auspicious', time: '03:30 AM – 05:00 AM', hi: 'शुभ', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
                  { name: 'Amrit', type: 'Auspicious', time: '05:00 AM – 06:30 AM', hi: 'अमृत', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' }
                ].map((slot, sIdx) => (
                  <div key={sIdx} className={`p-3 rounded-xl border ${slot.color} shadow-2xs`}>
                    <div className="flex items-center justify-between mb-1">
                      <strong className="text-sm font-bold font-serif">{slot.hi} ({slot.name})</strong>
                      <span className="text-[10px] font-bold uppercase tracking-wider">{slot.type}</span>
                    </div>
                    <span className="text-[11px] font-mono block opacity-90">{slot.time}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: HORA SCHEDULE                                                     */}
      {/* ========================================================================= */}
      {activeTab === 'hora' && (
        <div className="space-y-4">
          <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between mb-3 border-b border-[#E2DED5] pb-2">
              <div>
                <h3 className="font-serif font-bold text-base text-[#174C4B]">
                  {isHi ? '२४-घंटे होरा चक्र' : '24-Hour Planetary Hora Sequence'}
                </h3>
                <p className="text-xs text-[#6B7280]">
                  {isHi
                    ? 'प्रत्येक घंटे के स्वामी ग्रह के अनुसार कार्यों की अनुकूलता'
                    : 'Governing planetary rulers for each planetary hour from sunrise'}
                </p>
              </div>
              <span className="text-xs font-mono text-[#B58A4A] font-bold">
                Sunrise: {panchang.sunrise}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 text-xs">
              {[
                { hour: '1st Hora', planet: 'Sun (Surya)', hi: 'सूर्य', activity: 'Government, Admin' },
                { hour: '2nd Hora', planet: 'Venus (Shukra)', hi: 'शुक्र', activity: 'Art, Finance, Beauty' },
                { hour: '3rd Hora', planet: 'Mercury (Budh)', hi: 'बुध', activity: 'Trade, Accounts, Tech' },
                { hour: '4th Hora', planet: 'Moon (Chandra)', hi: 'चन्द्र', activity: 'Travel, Food, Liquids' },
                { hour: '5th Hora', planet: 'Saturn (Shani)', hi: 'शनि', activity: 'Machinery, Iron, Labor' },
                { hour: '6th Hora', planet: 'Jupiter (Guru)', hi: 'गुरु', activity: 'Education, Wisdom, Puja' },
                { hour: '7th Hora', planet: 'Mars (Mangal)', hi: 'मंगल', activity: 'Engineering, Energy' },
                { hour: '8th Hora', planet: 'Sun (Surya)', hi: 'सूर्य', activity: 'Contracts, Authority' },
                { hour: '9th Hora', planet: 'Venus (Shukra)', hi: 'शुक्र', activity: 'Luxury, Relationships' },
                { hour: '10th Hora', planet: 'Mercury (Budh)', hi: 'बुध', activity: 'Documentation, Trade' },
                { hour: '11th Hora', planet: 'Moon (Chandra)', hi: 'चन्द्र', activity: 'Reflection, Domestic' },
                { hour: '12th Hora', planet: 'Saturn (Shani)', hi: 'शनि', activity: 'Rest, Routine tasks' }
              ].map((hora, hIdx) => (
                <div key={hIdx} className="p-2.5 rounded-xl bg-[#FAF6ED] border border-[#E2DED5] space-y-1">
                  <span className="text-[10px] uppercase font-bold text-[#6B7280] block font-mono">{hora.hour}</span>
                  <strong className="text-xs font-bold text-[#174C4B] block">{hora.planet}</strong>
                  <span className="text-[10px] text-[#B58A4A] block truncate">{hora.activity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: ADVANCED EPHEMERIS, LONGITUDES & AYANAMSHA                          */}
      {/* ========================================================================= */}
      {activeTab === 'ephemeris' && (
        <div className="space-y-4">
          <div className="bg-[#FFFDF7] border border-[#E2DED5] rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between mb-3 border-b border-[#E2DED5] pb-2">
              <div>
                <h3 className="font-serif font-bold text-base text-[#174C4B]">
                  {isHi ? 'ग्रह स्पष्ट मान एवं आयनांश आंकड़े' : 'Astronomical Ephemeris & Ayanamsha'}
                </h3>
                <p className="text-xs text-[#6B7280]">
                  {isHi
                    ? 'VSOP87 / ELP2000-82b उच्च परिशुद्धता खगोलीय आंकड़े'
                    : 'High precision planetary longitudes & astronomical coordinates'}
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-900 border border-emerald-200 text-xs font-mono font-bold">
                Chitra Paksha / Lahiri
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mb-4">
              <div className="p-3 rounded-xl bg-[#FAF6ED] border border-[#E2DED5] space-y-1">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Sun Longitude (Nirayana)</span>
                <strong className="text-sm font-bold font-mono text-[#174C4B]">
                  {rawPanchanga ? `${rawPanchanga.ephemeris.sunLongitude.toFixed(3)}°` : 'N/A'}
                </strong>
                <span className="text-[10px] text-[#6B7280] block">
                  Declination: {rawPanchanga?.ephemeris.sunDeclination ? `${rawPanchanga.ephemeris.sunDeclination.toFixed(2)}°` : '0°'}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-[#FAF6ED] border border-[#E2DED5] space-y-1">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Moon Longitude (Nirayana)</span>
                <strong className="text-sm font-bold font-mono text-[#174C4B]">
                  {rawPanchanga ? `${rawPanchanga.ephemeris.moonLongitude.toFixed(3)}°` : 'N/A'}
                </strong>
                <span className="text-[10px] text-[#6B7280] block">
                  Declination: {rawPanchanga?.ephemeris.moonDeclination ? `${rawPanchanga.ephemeris.moonDeclination.toFixed(2)}°` : '0°'}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-[#FAF6ED] border border-[#E2DED5] space-y-1">
                <span className="text-[10px] uppercase font-bold text-[#6B7280] block">Ayanamsha (Lahiri)</span>
                <strong className="text-sm font-bold font-mono text-[#174C4B]">
                  {rawPanchanga ? `${rawPanchanga.ephemeris.ayanamsha.toFixed(4)}°` : '24.22°'}
                </strong>
                <span className="text-[10px] text-[#B58A4A] block">
                  System: {rawPanchanga?.ephemeris.ayanamshaSystem || 'Chitrapaksha'}
                </span>
              </div>
            </div>

            {/* Calendric Epochs */}
            <div className="p-3 rounded-xl bg-[#FAF6ED] border border-[#E2DED5] space-y-1.5 text-xs">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#174C4B] block border-b border-[#E2DED5] pb-1">
                {isHi ? 'वैदिक संवत एवं काल गणना सारणी' : 'Vedic Calendric Epoch Equivalents'}
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div><span className="text-[#6B7280]">Vikram Samvat:</span> <strong>{panchang.vikramSamvat}</strong></div>
                <div><span className="text-[#6B7280]">Shaka Samvat:</span> <strong>{panchang.shakaSamvat}</strong></div>
                <div><span className="text-[#6B7280]">Kali Samvat:</span> <strong>{panchang.vikramSamvat + 3102}</strong></div>
                <div><span className="text-[#6B7280]">Lunar Month:</span> <strong>{panchang.masa}</strong></div>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default FullPanchangView;
